import React, { useRef, useState } from 'react';
import Brand from './Brand';
import HeaderNav from './HeaderNav';
import { authApi } from '../services/api';

const MAX_IMAGE_BYTES = 2 * 1024 * 1024;
const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

const readImage = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve({ content_type: file.type, data: reader.result.split(',')[1] });
  reader.onerror = () => reject(new Error('Could not read the image. Please select it again.'));
  reader.onabort = () => reject(new Error('Image reading was cancelled. Please select it again.'));
  reader.readAsDataURL(file);
});

const HelpPage = ({ user, onBack, onLogout }) => {
  const [subject, setSubject] = useState('General Questions');
  const [message, setMessage] = useState('');
  const [image, setImage] = useState(null);
  const [imageError, setImageError] = useState('');
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const fileInput = useRef(null);
  const submitting = useRef(false);
  const email = user?.email || user?.username || '';

  const selectImage = (event) => {
    const file = event.target.files?.[0];
    setSent(false);
    setImage(null);
    setImageError('');
    if (!file) return;
    if (!IMAGE_TYPES.includes(file.type) || file.size === 0 || file.size > MAX_IMAGE_BYTES) {
      setImageError('Choose a PNG, JPEG, or WebP image no larger than 2 MB.');
      event.target.value = '';
      return;
    }
    setImage(file);
  };

  const submit = async (event) => {
    event.preventDefault();
    if (submitting.current) return;
    setError('');
    setSent(false);
    if (!message.trim()) {
      setError('Please enter a message.');
      return;
    }
    if (imageError) return;
    submitting.current = true;
    setSending(true);
    try {
      const attachment = image ? await readImage(image) : null;
      await authApi.contactSupport({ subject, message: message.trim(), image: attachment });
      setSent(true);
      setMessage('');
      setImage(null);
      if (fileInput.current) fileInput.current.value = '';
    } catch (err) {
      setError(err.message);
    } finally {
      submitting.current = false;
      setSending(false);
    }
  };

  return (
    <div className="app-shell">
      <header className="topbar">
        <Brand href="/" onClick={(event) => {
          if (onBack && !event.metaKey && !event.ctrlKey && !event.shiftKey && event.button === 0) {
            event.preventDefault();
            onBack();
          }
        }} />
        <HeaderNav user={user} onWorkspace={onBack} onLogout={onLogout} />
      </header>
      <main className="workspace help-workspace">
        <section className="workspace-intro">
          <div><p className="eyebrow">We're here to help</p><h1>Help.</h1></div>
          <p className="intro-copy">Ask a question, report an issue, or suggest a feature.</p>
        </section>
        <section className="panel help-panel">
          <div className="panel-heading"><h2>Contact support</h2></div>
          <p className="muted">Your message will be emailed to <a href="mailto:greg@hffinancial.com">greg@hffinancial.com</a>. Replies will go to your account email.</p>
          <p className="help-account">Sending as <strong>{email}</strong></p>
          <p className="muted">Please do not include passwords, account numbers, or other sensitive information in your message or screenshot.</p>
          {error && <div className="error-box" role="alert">{error}</div>}
          {sent && <div className="help-success" role="status">Your message has been sent to support. We'll reply to {email}.</div>}
          <form className="help-form" onSubmit={submit}>
            <fieldset disabled={sending}>
              <label htmlFor="help-subject">Subject</label>
              <select id="help-subject" value={subject} onChange={(event) => { setSubject(event.target.value); setSent(false); }}>
                <option>Feature request</option>
                <option>Bug report</option>
                <option>General Questions</option>
              </select>
              <label htmlFor="help-message">Message</label>
              <textarea id="help-message" required maxLength={10000} rows={8} value={message} onChange={(event) => { setMessage(event.target.value); setSent(false); }} placeholder="Tell us how we can help. For bugs, include what happened and the steps to reproduce it." />
              <label htmlFor="help-image">Image (optional)</label>
              <input id="help-image" ref={fileInput} type="file" accept="image/png,image/jpeg,image/webp" aria-describedby="help-image-hint" onChange={selectImage} />
              <p id="help-image-hint" className="muted">One PNG, JPEG, or WebP image, up to 2 MB.</p>
              {imageError && <div className="error-box" role="alert">{imageError}</div>}
              {image && <div className="help-attachment"><span>{image.name}</span><button type="button" className="text-action" onClick={() => { setImage(null); fileInput.current.value = ''; }}>Remove image</button></div>}
              <button type="submit" className="primary-button" disabled={!email || !!imageError}>{sending ? 'Sending...' : 'Send message'}</button>
            </fieldset>
          </form>
        </section>
      </main>
    </div>
  );
};

export default HelpPage;
